/**
 * <ai-contact-form>
 *
 * A custom element that handles AJAX form submission to Basin.
 * Follows @tailwindplus/elements conventions: light DOM, declarative
 * attributes, progressive enhancement.
 *
 * Usage:
 *   <ai-contact-form action="https://usebasin.com/f/xxx">
 *     <form> ... </form>
 *   </ai-contact-form>
 *
 * Attributes:
 *   action   - Basin form endpoint URL (required)
 *
 * Data attributes (set on the element, style with CSS):
 *   data-submitting  - Present while the form is being submitted
 *   data-success     - Present after successful submission
 *   data-error       - Present after a failed submission
 *
 * Fallback:
 *   JS disabled → form submits normally to Basin (full page redirect).
 */
class AiContactForm extends HTMLElement {
  #form = null
  #handleSubmit = null

  get action() {
    return this.getAttribute("action") || ""
  }

  connectedCallback() {
    // Defer setup: connectedCallback fires before child elements are
    // parsed, so this.querySelector("form") would return null.
    // requestAnimationFrame ensures the DOM children are available.
    requestAnimationFrame(() => {
      this.#form = this.querySelector("form")
      if (!this.#form) return

      // Override form action for progressive enhancement
      this.#form.setAttribute("action", this.action)

      this.#handleSubmit = this.#onSubmit.bind(this)
      this.#form.addEventListener("submit", this.#handleSubmit)
    })
  }

  disconnectedCallback() {
    if (this.#form && this.#handleSubmit) {
      this.#form.removeEventListener("submit", this.#handleSubmit)
    }
  }

  async #onSubmit(event) {
    event.preventDefault()
    event.stopPropagation()

    const form = this.#form
    const submitBtn = form.querySelector('button[type="submit"]')
    if (submitBtn) submitBtn.disabled = true

    const data = new FormData(form)

    // Clear previous state
    this.removeAttribute("data-error")
    this.removeAttribute("data-success")
    this.setAttribute("data-submitting", "")

    try {
      const response = await fetch(this.action, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: data,
      })

      this.removeAttribute("data-submitting")

      if (response.ok) {
        this.setAttribute("data-success", "")
        form.reset()
        // Toggle visibility directly — CSS class specificity can't
        // be relied on (Tailwind utility `hidden` beats base layer)
        form.style.display = "none"
        const success = this.querySelector("[data-contact-success]")
        if (success) success.classList.remove("hidden")
      } else {
        this.setAttribute("data-error", "")
        const error = this.querySelector("[data-contact-error]")
        if (error) error.classList.remove("hidden")
      }
    } catch {
      this.removeAttribute("data-submitting")
      this.setAttribute("data-error", "")
      const error = this.querySelector("[data-contact-error]")
      if (error) error.classList.remove("hidden")
    }
  }
}

customElements.define("ai-contact-form", AiContactForm)
